import React, { useContext, useEffect, useState } from 'react';
import { BiDownload, BiEdit, BiHistory, BiPlay, BiSolidFilePdf, BiUpload, BiX } from 'react-icons/bi';
import apiUser from '../../../../apiUser';
import { ContratosContext } from '../../../../contexts/ContratosProvider';
import { UserContext } from '../../../../contexts/userProvider';
import DataTable from '../../../Table';
import LoadingSpinner from '../../../Loader/load';
import EventosContrato from './EventosContrato';
import AtivarContrato, { botaoAtivarCls } from './AtivarContrato';
import { rotuloStatusContrato, rotuloTipoContrato, opcoesStatusContrato, contratoNuncaHomologado, buscaContrato } from './contratoStatus';
import CamposEventoEdicao from './CamposEventoEdicao';
import { exigeEventoValorGlobal, eventoVazio, eventoCompleto, payloadEvento, tipoSugerido, tiposPermitidos } from './edicaoValor';

const TIPOS = ['itens', 'global'];
const contratoVazio = { numero: '', nome: '', tipo: 'itens', valor: '', data_inicio: '', data_fim: '', status: 'implantacao', observacoes: '' };

const fmtData = (d) => (d ? String(d).slice(0, 10) : '');
const fmtValor = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
// Coluna DATE: os 10 primeiros caracteres valem → "dd/mm/aa".
const fmtDataCurta = (d) => {
    const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : '';
};
const fmtVigencia = (ct) => {
    const ini = fmtDataCurta(ct.data_inicio);
    const fim = fmtDataCurta(ct.data_fim);
    return ini || fim ? `${ini || '…'} – ${fim || '…'}` : '—';
};
const globalSemValor = (ct) => ct.tipo === 'global' && !(Number(ct.valor) > 0);

const STATUS_CLS = {
    implantacao: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    ativo: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    suspenso: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
    encerrado: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
};

const StatusBadge = ({ status }) => (
    <span className={`inline-block whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-semibold first-letter:uppercase ${STATUS_CLS[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
        {rotuloStatusContrato(status)}
    </span>
);

const inputCls = "w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-vox-blue-500 focus:border-vox-blue-500 transition-colors duration-200";
const labelCls = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2";
const focoCls = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vox-blue-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800";
const acaoCls = `inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${focoCls}`;
const acaoNeutraCls = `${acaoCls} border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700`;
const acaoEventosCls = `${acaoCls} border border-vox-blue-200 bg-vox-blue-50 text-vox-blue-800 hover:bg-vox-blue-100 dark:border-vox-blue-700 dark:bg-vox-blue-900/40 dark:text-vox-blue-100 dark:hover:bg-vox-blue-800/60`;

const Contratos = ({ client }) => {
    const { isGerente } = useContext(UserContext);
    const { contratos, loading, getContratosByClient, createContrato, updateContrato,
        uploadArquivo, downloadArquivo } = useContext(ContratosContext);
    const [editando, setEditando] = useState(null); // null | contratoVazio (novo) | contrato existente
    const [totalImplantado, setTotalImplantado] = useState(null); // contrato tipo itens: valor derivado
    const [eventosDe, setEventosDe] = useState(null); // contrato com modal de Eventos aberto
    const [ativarDe, setAtivarDe] = useState(null); // contrato com modal Ativar aberto
    const [enviandoArquivo, setEnviandoArquivo] = useState(false);
    // Valor Global de contrato ATIVO mudou → vira evento (09/out/2026): motivo, vigência, tipo, termo.
    const [eventoValor, setEventoValor] = useState(eventoVazio());
    useEffect(() => { setEventoValor(eventoVazio()); }, [editando?.id]);
    // Versão atual (da lista) do contrato: anexo e status mudam depois de abrir o modal.
    const atual = (ct) => (ct && ct.id ? contratos.find((c) => c.id === ct.id) || ct : ct);

    useEffect(() => { if (client?.id) getContratosByClient(client.id); }, [client?.id]);

    // Contrato tipo itens em edição: o valor é DERIVADO (total implantado dos
    // itens mensais, competência do disparo — mesma conta da tela de Itens).
    useEffect(() => {
        if (!editando?.id || editando.tipo !== 'itens') { setTotalImplantado(null); return; }
        const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
        const competencia = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        apiUser.post('/itens/dados', { id_cliente: client.id, competencia })
            .then(({ data }) => {
                if (!data.success) return setTotalImplantado(null);
                const normalize = (t) => (t || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
                const total = data.dados
                    .filter((i) => Number(i.id_contrato) === Number(editando.id) && normalize(i.unidade) !== 'unica')
                    .reduce((a, i) => a + (parseFloat(i.valor_unitario) || 0) * (parseFloat(i.quantidade_implantada) || 0), 0);
                setTotalImplantado(total);
            })
            .catch(() => setTotalImplantado(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editando?.id, editando?.tipo]);

    const salvar = async () => {
        // Mudar o status PARA encerrado pede confirmação (o botão Encerrar saiu da tabela).
        const original = editando.id ? contratos.find((c) => c.id === editando.id) : null;
        if (editando.status === 'encerrado' && original && original.status !== 'encerrado'
            && !window.confirm(`Encerrar o contrato "${editando.nome || original.nome}"? Ele deixa de faturar.`)) {
            return;
        }
        const dados = {
            ...editando,
            cliente_id: client.id,
            data_inicio: fmtData(editando.data_inicio) || null,
            data_fim: fmtData(editando.data_fim) || null
        };
        if (exigeEventoValorGlobal(original, editando)) dados.evento = payloadEvento(eventoValor);
        const ok = editando.id ? await updateContrato(dados) : await createContrato(dados);
        if (ok) setEditando(null);
    };

    if (!client) return null;

    const enviarArquivo = async (e) => {
        const arquivo = e.target.files[0];
        e.target.value = '';
        if (!arquivo || !editando?.id) return;
        setEnviandoArquivo(true);
        try { await uploadArquivo(editando.id, arquivo, client.id); } finally { setEnviandoArquivo(false); }
    };

    // Com algum contrato em implantação, as linhas sem ATIVAR reservam o espaço
    // do botão: Editar/Eventos ficam alinhados em todas as linhas.
    const algumEmImplantacao = contratos.some((c) => c.status === 'implantacao');

    const headers = [
        {
            label: 'Contrato', key: 'nome', sortable: true, wrapText: true, render: (ct) => (
                <div className="min-w-0">
                    <p className="font-semibold leading-snug text-gray-900 dark:text-white">{ct.nome}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>{rotuloTipoContrato(ct.tipo)}</span>
                        {ct.numero && <><span aria-hidden="true">·</span><span>nº {ct.numero}</span></>}
                        {ct.tipo !== 'global' && (
                            <><span aria-hidden="true">·</span><span>{Number(ct.qtd_itens) || 0} {Number(ct.qtd_itens) === 1 ? 'item' : 'itens'}</span></>
                        )}
                        {globalSemValor(ct) && (
                            <span className="ml-0.5 inline-block rounded-full bg-yellow-100 px-2 py-0.5 font-semibold text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200"
                                title="Defina o Valor Global para o faturamento por e-mail">
                                sem valor
                            </span>
                        )}
                    </p>
                </div>
            )
        },
        {
            label: 'Valor', key: 'valor', width: 'w-px', render: (ct) => (
                ct.tipo === 'global'
                    ? <span className="tabular-nums">{globalSemValor(ct) ? '—' : fmtValor(ct.valor)}</span>
                    : (
                        <span className="inline-flex flex-col items-center leading-tight" title="Soma dos itens do contrato (valor unitário × quantidade contratada), não é o valor mensal faturado">
                            <span className="tabular-nums">{fmtValor(ct.total_itens)}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">contratado</span>
                        </span>
                    )
            )
        },
        { label: 'Vigência', key: 'data_inicio', width: 'w-px', render: (ct) => <span className="tabular-nums">{fmtVigencia(ct)}</span> },
        { label: 'Status', key: 'status', width: 'w-px', render: (ct) => <StatusBadge status={ct.status} /> },
        {
            label: 'Ações', key: 'id', width: 'w-px', render: (ct) => (
                isGerente ? (
                    <div className="flex items-center justify-center gap-1.5">
                        <button type="button" onClick={() => setEditando({ ...ct })} className={acaoNeutraCls}>
                            <BiEdit className="h-3.5 w-3.5" aria-hidden="true" />Editar
                        </button>
                        <button type="button" onClick={() => setEventosDe(ct)} className={acaoEventosCls}
                            title="Reajustes, aditivos, supressões, renovações e a ativação do contrato">
                            <BiHistory className="h-3.5 w-3.5" aria-hidden="true" />Eventos
                        </button>
                        {ct.status === 'implantacao' ? (
                            <button type="button" onClick={() => setAtivarDe(ct)} className={`${botaoAtivarCls} px-2.5 py-1.5 text-xs`}
                                title="Ativar o contrato (Homologação da Instalação)">
                                <BiPlay className="h-3.5 w-3.5" aria-hidden="true" />ATIVAR
                            </button>
                        ) : algumEmImplantacao && (
                            <span aria-hidden="true" className={`${botaoAtivarCls} invisible px-2.5 py-1.5 text-xs`}>
                                <BiPlay className="h-3.5 w-3.5" />ATIVAR
                            </span>
                        )}
                    </div>
                ) : ct.arquivo_nome ? (
                    <button type="button" onClick={() => downloadArquivo(ct)} className={acaoNeutraCls} title={ct.arquivo_nome}>
                        <BiDownload className="h-3.5 w-3.5" aria-hidden="true" />PDF
                    </button>
                ) : <span className="text-gray-400">—</span>
            )
        }
    ];

    const editandoAtual = atual(editando);
    // Contrato como está no banco (a lista) e se a edição do Valor Global vira evento.
    const original = editando?.id ? contratos.find((c) => c.id === editando.id) : null;
    const exigeValor = exigeEventoValorGlobal(original, editando);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">📄</span>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Contratos do Cliente
                        </h2>
                    </div>
                    {isGerente && !editando && (
                        <button
                            type="button"
                            onClick={() => setEditando({ ...contratoVazio })}
                            className="px-6 py-3 bg-vox-green-600 hover:bg-vox-green-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">➕</span>
                            Novo contrato
                        </button>
                    )}
                </div>
            </div>

            {/* Modal de criação/edição (não fecha no clique fora: há dados digitados) */}
            {editando && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/50" />
                    <div role="dialog" aria-modal="true" aria-labelledby="editar-contrato-titulo"
                        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-800">
                        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                            <div className="min-w-0">
                                <h3 id="editar-contrato-titulo" className="text-xl font-bold text-vox-blue-900 dark:text-white">
                                    {editando.id ? 'Editar contrato' : 'Novo contrato'}
                                </h3>
                                <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                                    {editando.id ? editandoAtual.nome : client.nome}
                                </p>
                            </div>
                            <button type="button" onClick={() => setEditando(null)} aria-label="Fechar"
                                className={`-mr-2 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 ${focoCls}`}>
                                <BiX className="h-6 w-6" />
                            </button>
                        </header>

                        <div className="flex-1 min-h-0 overflow-auto px-6 py-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="lg:col-span-2">
                                    <label className={labelCls}>Nome</label>
                                    <input
                                        type="text"
                                        value={editando.nome}
                                        onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                                        required
                                        className={inputCls}
                                        placeholder="Ex.: Contrato Telefonia IP"
                                    />
                                </div>

                                <div>
                                    <label className={labelCls}>Número</label>
                                    <input
                                        type="text"
                                        value={editando.numero || ''}
                                        onChange={(e) => setEditando({ ...editando, numero: e.target.value })}
                                        className={inputCls}
                                        placeholder="Ex.: 042/2026"
                                    />
                                </div>

                                <div>
                                    <label className={labelCls}>Tipo</label>
                                    <select
                                        value={editando.tipo}
                                        onChange={(e) => setEditando({ ...editando, tipo: e.target.value })}
                                        className={inputCls}
                                    >
                                        {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>

                                <div>
                                    {editando.tipo === 'global' ? (
                                        <>
                                            <label className={labelCls}>Valor Global</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={editando.valor || ''}
                                                onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                                                className={inputCls}
                                                placeholder="0,00"
                                            />
                                            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>É o valor cobrado no relatório de faturamento por e-mail; sem ele, o contrato global não dispara.</p>
                                            {original && original.status === 'ativo' && original.tipo === 'global' && (
                                                <p className='text-xs text-amber-700 dark:text-amber-300 mt-1'>Contrato ativo: mudar o valor lança um evento (aditivo, supressão ou reajuste).</p>
                                            )}
                                            {original && original.status === 'ativo' && original.tipo !== 'global' && (
                                                <p className='text-xs text-amber-700 dark:text-amber-300 mt-1'>Contrato ativo: salve primeiro só o tipo Global (sem valor); depois informe o valor, que vira evento.</p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <label className={labelCls}>Valor (total implantado)</label>
                                            <p className={`${inputCls} bg-gray-50 dark:bg-gray-900`}>
                                                {editando.id ? (totalImplantado == null ? 'calculando…' : fmtValor(totalImplantado)) : '— derivado dos itens —'}
                                            </p>
                                            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>Derivado dos itens implantados do contrato (competência do disparo) — não editável.</p>
                                        </>
                                    )}
                                </div>

                                {exigeValor && (
                                    <div className="lg:col-span-3 md:col-span-2">
                                        <CamposEventoEdicao idPrefixo="valor-global" evento={eventoValor} onChange={setEventoValor}
                                            sugerido={tipoSugerido(original.valor, editando.valor)}
                                            permitidos={tiposPermitidos(original.valor, editando.valor)}
                                            aviso={`Contrato ativo: o Valor Global vai de ${fmtValor(original.valor)} para ${fmtValor(editando.valor)} por um evento na timeline, com proporcional a partir da data de vigência.`} />
                                    </div>
                                )}

                                <div>
                                    <label className={labelCls}>Dia de fechamento</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={editando.dia_fechamento || ''}
                                        onChange={(e) => setEditando({ ...editando, dia_fechamento: e.target.value ? Number(e.target.value) : null })}
                                        className={inputCls}
                                        placeholder="vazio = mês civil"
                                    />
                                    <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>Base do cálculo proporcional quando há evento (reajuste/aditivo) no meio do ciclo.</p>
                                </div>

                                <div>
                                    <label className={labelCls}>Data início</label>
                                    <input
                                        type="date"
                                        value={fmtData(editando.data_inicio)}
                                        onChange={(e) => setEditando({ ...editando, data_inicio: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>

                                <div>
                                    <label className={labelCls}>Data fim</label>
                                    <input
                                        type="date"
                                        value={fmtData(editando.data_fim)}
                                        onChange={(e) => setEditando({ ...editando, data_fim: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>

                                <div>
                                    <label className={labelCls}>Status</label>
                                    <select
                                        value={editando.status}
                                        onChange={(e) => setEditando({ ...editando, status: e.target.value })}
                                        className={inputCls}
                                    >
                                        {opcoesStatusContrato(
                                            editando.id ? (contratos.find((c) => c.id === editando.id)?.status || editando.status) : null,
                                            { nuncaHomologado: editando.id ? contratoNuncaHomologado(contratos.find((c) => c.id === editando.id)) : false },
                                        ).map((s) => <option key={s} value={s}>{rotuloStatusContrato(s)}</option>)}
                                    </select>
                                    {editando.status === 'implantacao' && (
                                        <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>Ativado pelo botão ATIVAR na lista de contratos (Homologação da Instalação). Até lá não fatura.</p>
                                    )}
                                </div>

                                <div className="lg:col-span-3">
                                    <label className={labelCls}>{editando.tipo === 'global' ? 'Descrição' : 'Observações'}</label>
                                    <textarea
                                        value={editando.observacoes || ''}
                                        onChange={(e) => setEditando({ ...editando, observacoes: e.target.value })}
                                        rows={3}
                                        className={inputCls}
                                        placeholder={editando.tipo === 'global' ? 'Descrição (aparece no relatório de faturamento por e-mail)' : 'Observações'}
                                    />
                                    {editando.tipo === 'global' && (
                                        <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>É a descrição da linha única do relatório enviado ao cliente; vazia, usa o nome do contrato.</p>
                                    )}
                                </div>

                                {editando.id && (
                                    <section aria-labelledby="arquivo-contrato-titulo"
                                        className="lg:col-span-3 md:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                                        <div className="flex min-w-0 items-center gap-3">
                                            <BiSolidFilePdf className={`h-8 w-8 shrink-0 ${editandoAtual.arquivo_nome ? 'text-red-600 dark:text-red-400' : 'text-gray-300 dark:text-gray-600'}`} aria-hidden="true" />
                                            <div className="min-w-0">
                                                <h4 id="arquivo-contrato-titulo" className="text-sm font-medium text-gray-700 dark:text-gray-300">Arquivo do contrato (PDF)</h4>
                                                <p className="truncate text-sm text-gray-900 dark:text-gray-100" title={editandoAtual.arquivo_nome || undefined}>
                                                    {editandoAtual.arquivo_nome || <span className="text-gray-500 dark:text-gray-400">Nenhum arquivo anexado</span>}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {editandoAtual.arquivo_nome && (
                                                <button type="button" onClick={() => downloadArquivo(editandoAtual)} className={acaoNeutraCls}>
                                                    <BiDownload className="h-3.5 w-3.5" aria-hidden="true" />Baixar
                                                </button>
                                            )}
                                            <label className={`${acaoEventosCls} cursor-pointer focus-within:ring-2 focus-within:ring-vox-blue-500 ${enviandoArquivo ? 'pointer-events-none opacity-60' : ''}`}>
                                                <BiUpload className="h-3.5 w-3.5" aria-hidden="true" />
                                                {enviandoArquivo ? 'Enviando…' : (editandoAtual.arquivo_nome ? 'Substituir' : 'Anexar')}
                                                <input type="file" accept="application/pdf" className="sr-only" onChange={enviarArquivo} disabled={enviandoArquivo} />
                                            </label>
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>

                        <footer className="flex flex-col-reverse gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-700 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={() => setEditando(null)}
                                className={`rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 ${focoCls}`}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={salvar}
                                disabled={exigeValor && !eventoCompleto(eventoValor)}
                                title={exigeValor && !eventoCompleto(eventoValor) ? 'Informe o motivo e a data de vigência da alteração de valor' : undefined}
                                className={`rounded-lg bg-vox-green-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-vox-green-700 disabled:cursor-not-allowed disabled:opacity-50 ${focoCls}`}
                            >
                                Salvar
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {/* Tabela de contratos */}
            {loading ? (
                <div className="flex items-center justify-center min-h-64">
                    <LoadingSpinner />
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <DataTable data={contratos} headers={headers} perPageItems={10} autoWidth customSearchFunction={buscaContrato} />
                </div>
            )}

            {eventosDe && (
                <EventosContrato
                    contrato={atual(eventosDe)}
                    client={client}
                    onClose={() => setEventosDe(null)}
                    onMudou={() => getContratosByClient(client.id)}
                />
            )}

            {ativarDe && (
                <AtivarContrato
                    contrato={ativarDe}
                    client={client}
                    onClose={() => setAtivarDe(null)}
                    onAtivado={() => { getContratosByClient(client.id); setAtivarDe(null); }}
                />
            )}
        </div>
    );
};

export default Contratos;
